import React from 'react'

import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Button, Form, Modal } from '_zari/core'
import Yup from '_zari/validation/_yup'

import {
  OrigenesContabilidadConstant,
  ModalidadCalculosDeDepreciacionConstant
} from 'constants/index'

import { IdGen, RounderUtil } from 'utils'

import * as FormGroups from './formGroups'

export const ValidationSchema = Yup.object().shape({
  _idCentroCostoNivel1: Yup.string().when('flagCentroCosto', {
    is: true,
    then: Yup.string().required()
  }),
  _idCentroCostoNivel2: Yup.string().when('flagCentroCosto', {
    is: true,
    then: Yup.string().required()
  }),
  cuentaContable: Yup.string().required(),
  importe: Yup.number().required(),
  tipoAfectacion: Yup.string().required()
})

const DetallesCabeceraSection = ({ show, handleClose, iOrigen, dispatch, edicionData }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const inputImporteRef = React.useRef(null)

  const formInitialValues = React.useMemo(() => {
    return {
      __keyCuentaContable: IdGen.newId(),
      _idFila: edicionData._idFila ?? '',
      _idCentroCostoNivel1: edicionData._idCentroCostoNivel1 ?? '',
      _idCentroCostoNivel2: edicionData._idCentroCostoNivel2 ?? '',
      _idActivoFijo: edicionData._idActivoFijo ?? '',
      tipoMovimientoActivo: edicionData.tipoMovimientoActivo ?? '2',
      centroCostoNivel1Info: edicionData.centroCostoNivel1Info ?? '',
      centroCostoNivel2Info: edicionData.centroCostoNivel2Info ?? '',
      cuentaContable: edicionData.cuentaContable ?? '',
      cuentaDestino: edicionData.cuentaDestino ?? '',
      cuentaDestinoInfo: edicionData.cuentaDestinoInfo ?? '',
      cuentaContableInfo: edicionData.cuentaContableInfo ?? '',
      importe: edicionData.importe ?? '',
      flagCentroCosto: edicionData.flagCentroCosto ?? false,
      esCuentaActivoFijo: edicionData.esCuentaActivoFijo ?? false,
      tipoAfectacion: edicionData.tipoAfectacion ?? '',
      noIncluyeIgv: edicionData.noIncluyeIgv ?? false
    }
  }, [edicionData])

  const aplicaDestino = React.useCallback(
    cuentaContable => {
      if (__adminContext.modalidadCalculoDeDepreciacion !== ModalidadCalculosDeDepreciacionConstant.CLASE9) {
        return false
      }

      if (!cuentaContable) {
        return false
      }

      if (
        cuentaContable.startsWith('61') ||
        cuentaContable.startsWith('62') ||
        cuentaContable.startsWith('63') ||
        cuentaContable.startsWith('64') ||
        cuentaContable.startsWith('65') ||
        cuentaContable.startsWith('67')
      ) {
        return true
      } else {
        return false
      }
    },
    [__adminContext.modalidadCalculoDeDepreciacion]
  )

  const onSubmit = React.useCallback(
    (data, { setSubmitting }) => {
      if (!data.noIncluyeIgv) {
        data.importe = RounderUtil.round(data.importe / 1.18)
      }

      if (aplicaDestino(data.cuentaContable) && !data.cuentaDestino) {
        __zariContext.snack('error', 'Ingresar cuenta destino.')
        setSubmitting(false)
        return
      }

      if (data.esCuentaActivoFijo) {
        if (!data.tipoMovimientoActivo || !data._idActivoFijo) {
          __zariContext.snack('error', 'Informacion de activo fijo es requerido.')
          setSubmitting(false)
          return
        }
      }

      dispatch({ type: 'actualizar', payload: data })

      handleClose()
    },
    [dispatch, aplicaDestino, __zariContext, handleClose]
  )

  return (
    <>

<Modal show={show} centered size='md'>
      <Modal.Header>
        <Modal.Title>EDICION</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={formInitialValues}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ errors, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => (
          <>
          <Modal.Body>
<Form>
                <FormGroups.ImporteFormGroup
                  innerRef={inputImporteRef}
                  iError={errors.importe}
                  iTouched={touched.importe}
                  fIsSubmitting={isSubmitting}
                  noIncluyeIgv={values.noIncluyeIgv}
                  vCuentaContable={values.cuentaContable}
                  vTipoAfectacion={values.tipoAfectacion}
                  vCentroCosto={values._idCentroCostoNivel1}
                  vFlagCentroCosto={values.flagCentroCosto}
                  vCuentaDestino={values.cuentaDestino}
                  aplicaDestino={aplicaDestino(values.cuentaContable)}
                />
                <FormGroups.CuentaContableFormGroup
                  key={values.__keyCuentaContable}
                  iError={errors.cuentaContable}
                  iTouched={touched.cuentaContable}
                  iValue={values.cuentaContableInfo}
                  setFieldValue={setFieldValue}
                />
                <FormGroups.TipoAfectacionFormGroup
                  iOrigen={iOrigen}
                  iError={errors.tipoAfectacion}
                  iTouched={touched.tipoAfectacion}
                  fIsSubmitting={isSubmitting}
                  flagCentroCosto={values.flagCentroCosto}
                  esCuentaActivoFijo={values.esCuentaActivoFijo}
                  aplicaDestino={aplicaDestino(values.cuentaContable)}
                />
                {values.esCuentaActivoFijo ? (
                    <FormGroups.ActivoFijoFormGroup
                      iError={errors._idActivoFijo}
                      iTouched={touched._idActivoFijo}
                      iCuentaContable={values.cuentaContable}
                      fIsSubmitting={isSubmitting}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                    />
                ) : values.flagCentroCosto ? (
                  <>
                  <FormGroups.CentroCostoNivel1FormGroup
                    iError={errors._idCentroCostoNivel1}
                    iTouched={touched._idCentroCostoNivel1}
                    fIsSubmitting={isSubmitting}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                  {values._idCentroCostoNivel1 && (
                      <FormGroups.CentroCostoNivel2FormGroup
                        iError={errors._idCentroCostoNivel2}
                        iTouched={touched._idCentroCostoNivel2}
                        setFieldValue={setFieldValue}
                        iValue={values.centroCostoNivel2Info}
                        idNodoPadre={values._idCentroCostoNivel1}
                        vOrigen={iOrigen}
                        aplicaDestino={aplicaDestino(values.cuentaContable)}
                      />
                  )}
                  </>
                ) : null}
                {iOrigen === OrigenesContabilidadConstant.COMPRAS && aplicaDestino(values.cuentaContable) ? (
                    <FormGroups.CuentaDestinoFormGroup
                      iError={errors.cuentaDestino}
                      iTouched={touched.cuentaDestino}
                      fIsSubmitting={isSubmitting}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                    />
                ) : null}
          </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button color='secondary' onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button id='modal-nuevoasiento-agregar-button' onClick={handleSubmit} disabled={isSubmitting}>
                Guardar
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
    </>
  )
}

export default React.memo(DetallesCabeceraSection)
